import {
  ApiCreateUniversityRequest,
  ApiDoiConfigTestResult,
  ApiUniversitiesResponse,
  ApiUniversity,
} from '@repo/types';
import { useQueryKeys } from '@src/hooks/useQueryKeys';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useApi } from './useApi';
import { useEnabled } from './useEnabled';

export function useGetUniversities({
  enabled = true,
  includeManagedUniversities = false,
}: {
  enabled?: boolean;
  includeManagedUniversities?: boolean;
} = {}) {
  const api = useApi();
  const queryEnabled = useEnabled(enabled);
  const queryKey = useQueryKeys(['universities', !!includeManagedUniversities]);
  const fetchUniversities = async () => {
    const queryParams = new URLSearchParams();
    if (includeManagedUniversities) {
      queryParams.set('includeManagedUniversities', 'true');
    }
    const url = queryParams.toString()
      ? `/universities?${queryParams.toString()}`
      : '/universities';
    const response = await api.get(url);
    const universitiesResponse =
      (await response?.json()) as ApiUniversitiesResponse;
    return universitiesResponse.universities;
  };

  return useQuery(queryKey, fetchUniversities, {
    enabled: queryEnabled,
  });
}

export function useGetUserUniversities({
  includeManagedUniversities = false,
}: {
  includeManagedUniversities?: boolean;
} = {}) {
  const api = useApi();
  const queryKey = useQueryKeys([
    'user-universities',
    includeManagedUniversities,
  ]);
  const queryEnabled = useEnabled();

  const fetchUserUniversities = async () => {
    const queryParams = new URLSearchParams();
    if (includeManagedUniversities) {
      queryParams.set('includeManagedUniversities', 'true');
    }
    const response = await api.get(
      `/universities/user?${queryParams.toString()}`
    );
    const universitiesResponse =
      (await response?.json()) as ApiUniversitiesResponse;
    return universitiesResponse.universities;
  };
  return useQuery(queryKey, fetchUserUniversities, { enabled: queryEnabled });
}

export function useGetUniversity(id: string | undefined) {
  const api = useApi();
  const queryEnabled = !!id;

  const fetchUniversity = async () => {
    const response = await api.get(`/universities/${id}`, { skipAuth: true });
    return (await response?.json()) as ApiUniversity;
  };
  return useQuery(['university', id], fetchUniversity, {
    enabled: queryEnabled,
  });
}

export function useCreateUniversity() {
  const api = useApi();
  const queryClient = useQueryClient();
  const universitiesQueryKey = useQueryKeys(['universities']);

  const createUniversity = async (data: ApiCreateUniversityRequest) => {
    await api.post('/universities', data);
  };

  return useMutation({
    mutationFn: createUniversity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: universitiesQueryKey });
    },
  });
}

export function useDeleteUniversity() {
  const api = useApi();
  const queryClient = useQueryClient();
  const universitiesQueryKey = useQueryKeys(['universities']);

  const deleteUniversity = async (universityId: string) => {
    await api.del(`/universities/${universityId}`);
  };

  return useMutation({
    mutationFn: deleteUniversity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: universitiesQueryKey });
    },
  });
}

export function useGetUniversityByInstitutionId(
  institutionId: string | undefined
) {
  const api = useApi();
  const queryEnabled = useEnabled(!!institutionId);

  const fetchUniversity = async (): Promise<ApiUniversity | null> => {
    if (!institutionId) return null;

    const response = await api.get(
      `/universities/institution/${encodeURIComponent(institutionId)}`
    );
    if (!response?.ok) {
      return null;
    }
    return (await response?.json()) as ApiUniversity;
  };

  return useQuery({
    queryKey: ['university', institutionId || ''],
    queryFn: fetchUniversity,
    enabled: queryEnabled,
  });
}

export function useUpdateDepartments() {
  const api = useApi();
  const queryClient = useQueryClient();
  const universitiesQueryKey = useQueryKeys(['universities']);

  const updateDepartments = async ({
    universityId,
    departments,
  }: {
    universityId: string;
    departments: string[];
  }) => {
    await api.put(`/universities/${universityId}/departments`, {
      departments,
    });
  };

  return useMutation({
    mutationFn: updateDepartments,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: universitiesQueryKey });
      queryClient.invalidateQueries({ queryKey: 'university' });
    },
  });
}

export function useTestDoiConfig(universityId: string | undefined) {
  const api = useApi();

  const testDoiConfig = async (params: {
    clientId: string;
    prefix: string;
    password?: string;
  }): Promise<ApiDoiConfigTestResult | null> => {
    if (!universityId) return null;
    const response = await api.post(
      `/universities/${universityId}/test-doi-config`,
      params
    );
    return (await response?.json()) as ApiDoiConfigTestResult;
  };

  return useMutation({ mutationFn: testDoiConfig });
}
